import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Tag, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { handleApiError, getApiErrorDetails } from '@/api/client';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import type { ItemType, ProductCollection, ProductionMethod, SupplyType } from '@/types/api';

export type TaxonomyKind = 'itemType' | 'collection' | 'productionMethod';

type TaxonomyItem = ItemType | ProductCollection | ProductionMethod;

interface TaxonomyModalProps {
  kind: TaxonomyKind;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  item?: TaxonomyItem | null;
  createFn: (data: Record<string, unknown>) => Promise<TaxonomyItem>;
  updateFn: (id: string, data: Record<string, unknown>) => Promise<TaxonomyItem>;
  queryKey: string;
}

interface FormData {
  name: string;
  description: string;
  icon: string;
  sortOrder: string;
  code: string;
  supplyTypes: SupplyType[];
  usesDesignFile: boolean;
  uses3dModel: boolean;
}

interface FormErrors {
  name?: string;
  code?: string;
  sortOrder?: string;
  submit?: string;
}

const KIND_CONFIG: Record<TaxonomyKind, {
  label: string;
  singular: string;
  namePlaceholder: string;
  descriptionPlaceholder: string;
  defaultIcon: string;
}> = {
  itemType: {
    label: 'Item Type',
    singular: 'item type',
    namePlaceholder: 'e.g., Keychain',
    descriptionPlaceholder: 'What physical form factor the customer is buying...',
    defaultIcon: '🏷️',
  },
  collection: {
    label: 'Collection',
    singular: 'collection',
    namePlaceholder: 'e.g., Pride Collection',
    descriptionPlaceholder: 'Audience or themed grouping for products...',
    defaultIcon: '✨',
  },
  productionMethod: {
    label: 'Production Method',
    singular: 'production method',
    namePlaceholder: 'e.g., UV Printed',
    descriptionPlaceholder: 'How this product is manufactured...',
    defaultIcon: '🏭',
  },
};

const SUPPLY_TYPE_OPTIONS: SupplyType[] = [
  // Blanks
  'DOG_TAG_BLANK', 'ACRYLIC_BLANK', 'ORNAMENT_BLANK', 'KEYCHAIN_BLANK',
  'TUMBLER_BLANK', 'MAGNET_BLANK', 'TROPHY_BLANK', 'TOKEN_BLANK',
  'SIGN_BLANK', 'BUTTON_BLANK', 'OTHER_BLANK',
  'SHIRT_BLANK', 'HOODIE_BLANK', 'HAT_BLANK', 'BLANK_APPAREL',
  // Consumables
  'FILAMENT', 'UV_INK', 'SUBLIMATION_INK', 'THREAD', 'VINYL',
  'HEAT_TRANSFER', 'TRANSFER_TAPE', 'LASER_MATERIAL', 'SUBLIMATION_PAPER',
  'ADHESIVE', 'RESIN', 'CLEANING_FLUID', 'OTHER_CONSUMABLE',
  // Hardware
  'KEY_RING', 'JUMP_RING', 'MAGNET', 'CHAIN', 'SCREW', 'BOLT', 'NUT',
  'LED', 'PIN_BACK', 'CLASP', 'FASTENER', 'OTHER_HARDWARE',
  // Other
  'PACKAGING', 'ELECTRONIC', 'RAW_MATERIAL', 'MAINTENANCE_PART',
  'ACCESSORY', 'OTHER',
];

function isProductionMethod(item: TaxonomyItem | null | undefined): item is ProductionMethod {
  return !!item && 'code' in item;
}

export function TaxonomyModal({
  kind,
  isOpen,
  onClose,
  onSuccess,
  item,
  createFn,
  updateFn,
  queryKey,
}: TaxonomyModalProps) {
  const queryClient = useQueryClient();
  const isEditMode = !!item;
  const config = KIND_CONFIG[kind];
  const isProduction = kind === 'productionMethod';

  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    icon: config.defaultIcon,
    sortOrder: '0',
    code: '',
    supplyTypes: [],
    usesDesignFile: false,
    uses3dModel: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        description: item.description || '',
        icon: item.icon || config.defaultIcon,
        sortOrder: item.sortOrder?.toString() || '0',
        code: isProductionMethod(item) ? item.code : '',
        supplyTypes: isProductionMethod(item) ? item.supplyTypes || [] : [],
        usesDesignFile: isProductionMethod(item) ? item.usesDesignFile : false,
        uses3dModel: isProductionMethod(item) ? item.uses3dModel : false,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        icon: config.defaultIcon,
        sortOrder: '0',
        code: '',
        supplyTypes: [],
        usesDesignFile: false,
        uses3dModel: false,
      });
    }
    setErrors({});
    setTouched({});
  }, [item, isOpen, config.defaultIcon]);

  const handleMutationError = (err: unknown) => {
    const errorDetails = getApiErrorDetails(err);

    if (errorDetails.isValidationError && Object.keys(errorDetails.fieldErrors).length > 0) {
      const fieldErrors: Partial<FormErrors> = {};
      Object.entries(errorDetails.fieldErrors).forEach(([field, message]) => {
        if (field === 'name') fieldErrors.name = message;
        else if (field === 'code') fieldErrors.code = message;
        else if (field === 'sortOrder') fieldErrors.sortOrder = message;
      });
      setErrors((prev) => ({ ...prev, ...fieldErrors, submit: 'Please fix the validation errors below.' }));
    } else {
      const message = handleApiError(err);
      setErrors((prev) => ({ ...prev, submit: message }));
    }
  };

  const createMutation = useMutation({
    mutationFn: createFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      onSuccess?.();
      handleClose();
    },
    onError: handleMutationError,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => updateFn(item!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      onSuccess?.();
      handleClose();
    },
    onError: handleMutationError,
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = `${config.label} name is required`;
    }

    if (isProduction && !formData.code.trim()) {
      newErrors.code = 'Code is required';
    }

    const sortOrder = parseInt(formData.sortOrder, 10);
    if (Number.isNaN(sortOrder) || sortOrder < 0) {
      newErrors.sortOrder = 'Sort order must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      icon: formData.icon.trim() || undefined,
      sortOrder: parseInt(formData.sortOrder, 10),
    };

    if (isProduction) {
      payload.code = formData.code.trim().toUpperCase();
      payload.supplyTypes = formData.supplyTypes;
      payload.usesDesignFile = formData.usesDesignFile;
      payload.uses3dModel = formData.uses3dModel;
    }

    return payload;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const data = buildPayload();
    if (isEditMode) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      icon: config.defaultIcon,
      sortOrder: '0',
      code: '',
      supplyTypes: [],
      usesDesignFile: false,
      uses3dModel: false,
    });
    setErrors({});
    setTouched({});
    createMutation.reset();
    updateMutation.reset();
    onClose();
  };

  const handleChange = (field: keyof FormData, value: string | boolean | SupplyType[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const toggleSupplyType = (type: SupplyType) => {
    setFormData((prev) => ({
      ...prev,
      supplyTypes: prev.supplyTypes.includes(type)
        ? prev.supplyTypes.filter((t) => t !== type)
        : [...prev.supplyTypes, type],
    }));
  };

  if (!isOpen) return null;

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/60 dark:bg-charcoal-950/80 backdrop-blur-sm animate-fade-in"
        onClick={!isPending ? handleClose : undefined}
      />

      <div className="relative w-full max-w-lg max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-charcoal-900 border border-gray-200 dark:border-charcoal-700 shadow-2xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-charcoal-700 bg-gray-50 dark:bg-charcoal-850">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg bg-amber-450/10 text-2xl">
              {formData.icon || config.defaultIcon}
            </div>
            <div>
              <h2 className="font-display text-xl font-bold text-gray-900 dark:text-warm-cream">
                {isEditMode ? `Edit ${config.label}` : `Add New ${config.label}`}
              </h2>
              <p className="text-sm text-gray-600 dark:text-warm-sand">
                {isEditMode ? `Update ${config.singular} details` : `Create a new ${config.singular}`}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isPending}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-xl transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-warm-sand" />
          </button>
        </div>

        {(errors.submit || errors.name || errors.code || errors.sortOrder || createMutation.isError || updateMutation.isError) && (
          <div className="mx-6 mt-4">
            <ErrorAlert
              title={errors.name || errors.code || errors.sortOrder ? 'Please fix the errors below' : `Failed to ${isEditMode ? 'update' : 'create'} ${config.singular}`}
              message={errors.submit}
              fieldErrors={{
                ...(errors.name && { name: errors.name }),
                ...(errors.code && { code: errors.code }),
                ...(errors.sortOrder && { sortOrder: errors.sortOrder }),
              }}
              severity={errors.name || errors.code || errors.sortOrder ? 'warning' : 'error'}
            />
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-5">
            {isProduction && (
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                  Code <span className="text-danger-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                  placeholder="e.g., UV_PRINTED"
                  className={`w-full px-4 py-3 bg-white dark:bg-charcoal-800 border rounded-xl text-gray-900 dark:text-warm-cream font-mono uppercase placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:ring-2 focus:ring-amber-450/30 transition-all ${
                    errors.code && touched.code ? 'border-danger-500' : 'border-gray-300 dark:border-charcoal-600 focus:border-amber-450'
                  }`}
                />
                {errors.code && touched.code && (
                  <p className="mt-1.5 text-sm text-danger-400 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.code}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Name <span className="text-danger-400">*</span>
              </label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-taupe" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder={config.namePlaceholder}
                  className={`w-full pl-10 pr-4 py-3 bg-white dark:bg-charcoal-800 border rounded-xl text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:ring-2 focus:ring-amber-450/30 transition-all ${
                    errors.name && touched.name ? 'border-danger-500' : 'border-gray-300 dark:border-charcoal-600 focus:border-amber-450'
                  }`}
                  autoFocus
                />
              </div>
              {errors.name && touched.name && (
                <p className="mt-1.5 text-sm text-danger-400 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={config.descriptionPlaceholder}
                rows={2}
                className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:ring-2 focus:ring-amber-450/30 focus:border-amber-450 transition-all resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Icon (emoji)
              </label>
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => handleChange('icon', e.target.value)}
                placeholder={config.defaultIcon}
                maxLength={4}
                className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream text-2xl focus:outline-none focus:ring-2 focus:ring-amber-450/30 focus:border-amber-450 transition-all"
              />
            </div>

            {isProduction && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center gap-3 p-4 bg-white dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.usesDesignFile}
                      onChange={(e) => handleChange('usesDesignFile', e.target.checked)}
                      className="w-4 h-4 accent-amber-450"
                    />
                    <span className="text-sm text-gray-900 dark:text-warm-cream">Uses design file</span>
                  </label>
                  <label className="flex items-center gap-3 p-4 bg-white dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.uses3dModel}
                      onChange={(e) => handleChange('uses3dModel', e.target.checked)}
                      className="w-4 h-4 accent-amber-450"
                    />
                    <span className="text-sm text-gray-900 dark:text-warm-cream">Uses 3D model</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                    Supply Types
                  </label>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-3 bg-white dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl">
                    {SUPPLY_TYPE_OPTIONS.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleSupplyType(type)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                          formData.supplyTypes.includes(type)
                            ? 'bg-amber-450/10 border-amber-450/40 text-amber-600 dark:text-amber-400'
                            : 'border-gray-300 dark:border-charcoal-600 text-gray-600 dark:text-warm-sand hover:border-charcoal-500'
                        }`}
                      >
                        {type.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Sort Order
              </label>
              <input
                type="number"
                min="0"
                value={formData.sortOrder}
                onChange={(e) => handleChange('sortOrder', e.target.value)}
                className={`w-full px-4 py-3 bg-white dark:bg-charcoal-800 border rounded-xl text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:ring-2 focus:ring-amber-450/30 transition-all ${
                  errors.sortOrder ? 'border-danger-500' : 'border-gray-300 dark:border-charcoal-600 focus:border-amber-450'
                }`}
              />
              {errors.sortOrder && (
                <p className="mt-1.5 text-sm text-danger-400">{errors.sortOrder}</p>
              )}
              <p className="mt-1.5 text-xs text-gray-600 dark:text-warm-sand">
                Lower numbers appear first in the list.
              </p>
            </div>

            <div className="p-4 bg-white dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl">
              <p className="text-xs text-gray-600 dark:text-warm-sand mb-3">Preview</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-450/10 text-xl">
                  {formData.icon || config.defaultIcon}
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-warm-cream">
                    {formData.name || `${config.label} Name`}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-warm-sand">
                    {formData.description || 'No description'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-charcoal-700">
            <button type="button" onClick={handleClose} className="btn-secondary" disabled={isPending}>
              Cancel
            </button>
            <button type="submit" className="btn-primary min-w-[140px]" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditMode ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {isEditMode ? 'Save Changes' : `Create ${config.label}`}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
